import React from 'react'
import { cn } from './cn'

export interface RadioOption {
  value: string
  label: React.ReactNode
  description?: string
  disabled?: boolean
}

export interface RadioGroupProps {
  name: string
  value: string
  onChange: (value: string) => void
  options: RadioOption[]
  orientation?: 'vertical' | 'horizontal'
  /** Required accessible name for the radiogroup. */
  label: string
  className?: string
}

export type RadioProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { className, ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      type="radio"
      className={cn(
        'size-[18px] shrink-0 appearance-none rounded-full border border-border bg-background checked:border-[5px] checked:border-foreground focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)] focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-40',
        className
      )}
      {...rest}
    />
  )
})

export function RadioGroup({
  name,
  value,
  onChange,
  options,
  orientation = 'vertical',
  label,
  className,
}: RadioGroupProps) {
  const refs = React.useRef(new Map<string, HTMLInputElement>())

  const move = (currentValue: string, direction: -1 | 1) => {
    const enabled = options.filter((option) => !option.disabled)
    if (enabled.length === 0) return
    const index = Math.max(0, enabled.findIndex((option) => option.value === currentValue))
    const next = enabled[(index + direction + enabled.length) % enabled.length]
    if (!next) return
    onChange(next.value)
    queueMicrotask(() => refs.current.get(next.value)?.focus())
  }

  return (
    <div
      className={cn(orientation === 'horizontal' ? 'flex flex-wrap gap-3' : 'grid gap-2', className)}
      role="radiogroup"
      aria-label={label}
    >
      {options.map((option) => (
        <label key={option.value} className="inline-flex min-h-6 cursor-pointer items-start gap-2 text-base text-foreground has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-40">
          <Radio
            ref={(node) => {
              if (node) refs.current.set(option.value, node)
              else refs.current.delete(option.value)
            }}
            id={`${name}-${option.value}`}
            name={name}
            value={option.value}
            checked={option.value === value}
            disabled={option.disabled}
            onChange={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                event.preventDefault()
                move(option.value, 1)
              }
              if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                event.preventDefault()
                move(option.value, -1)
              }
            }}
          />
          <span className="min-w-0">
            <span className="block">{option.label}</span>
            {option.description ? <span className="mt-0.5 block text-xs text-muted">{option.description}</span> : null}
          </span>
        </label>
      ))}
    </div>
  )
}
